import { useAuth } from '@/auth/useAuth';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Button } from '@/components/ui';
import { Sello, estadoAudio } from '@/components/hallazgos/Sello';
import { type AudioSysadminDTO, sysadminService } from '@/services/sysadmin.service';
import { ChevronDown, ChevronRight, Copy, Download, ExternalLink, FileUp, Headphones, Search, Trash2, Users } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Gestión de las asesorías con audios subidos por otro asesor, a partir del JSONL que deja
 * scripts/actas/analizar-audios-ajenos.ts. Muestra quién subió cada audio y cuándo se guardaron las
 * ternas para decidir: cambiar el asesor, separar la asesoría en dos o partir las evaluaciones
 * internas en la fecha en que llegó el otro asesor. No toca la base de datos: la decisión y las notas
 * se guardan en este navegador y se exportan como JSONL.
 */

type Decision = 'PENDIENTE' | 'CAMBIAR_ASESOR' | 'SEPARAR_ASESORIA' | 'SEPARAR_EVALUACIONES' | 'SIN_CAMBIOS' | 'RESUELTA';

interface AudioCaso {
  audioId: number;
  estado: string;
  vigente: boolean;
  fechaGrabacion: string | null;
  duracionSeg: number | null;
  subidoPorId: number | null;
  subidoPor: string | null;
  email: string | null;
  esDelDueno: boolean;
}

interface TernaCaso {
  id: number;
  ternaId: number;
  fechaEvaluacion: string;
}

interface CasoAudios {
  asesoriaId: number;
  empresaNit: string;
  empresaNombre: string;
  actaUrl?: string | null;
  orden?: number | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  dueno: { asesorId: number | null; nombre: string | null };
  subieron: string[];
  audios: AudioCaso[];
  resumenAudios: { total: number; vigentes: number; ajenosVigentes: number; primerAudioAjeno: string | null };
  evaluacionesInternas: {
    ternasEvaluadas: number;
    antesDelPrimerAudioAjeno: number;
    despues: number;
    primeraFecha: string | null;
    ultimaFecha: string | null;
    ternas: TernaCaso[];
  };
  sugerencia: string;
  decision?: Decision;
  nota?: string;
  actualizadoEn?: string;
}

const STORAGE_KEY = 'sysadmin.audiosAjenos';
const DECISIONES: { valor: Decision; label: string; variant: 'yellow' | 'red' | 'purple' | 'blue' | 'gray' | 'green' }[] = [
  { valor: 'PENDIENTE', label: 'Pendiente', variant: 'yellow' },
  { valor: 'CAMBIAR_ASESOR', label: 'Cambiar asesor', variant: 'red' },
  { valor: 'SEPARAR_ASESORIA', label: 'Separar asesoría', variant: 'purple' },
  { valor: 'SEPARAR_EVALUACIONES', label: 'Separar evaluaciones por fecha', variant: 'blue' },
  { valor: 'SIN_CAMBIOS', label: 'Dejar como está', variant: 'gray' },
  { valor: 'RESUELTA', label: 'Resuelta', variant: 'green' },
];
const SUGERENCIAS: Record<string, { label: string; variant: 'red' | 'purple' | 'yellow' }> = {
  CAMBIAR_ASESOR: { label: 'Cambiar asesor', variant: 'red' },
  SEPARAR_ASESORIA: { label: 'Separar', variant: 'purple' },
  REVISAR: { label: 'Revisar', variant: 'yellow' },
};

const fecha = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-CO') : '—');
const fechaHora = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('es-CO') : '—');
function duracion(seg?: number | null) {
  if (!seg) return '0s';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m ${seg % 60}s`;
}

/** Diferencia máxima (segundos) para considerar que dos audios pueden ser el mismo archivo. */
const TOLERANCIA_SEG = 2;

/** Todos los audios de la plataforma (en vivo) y los nombres de quien los subió, para buscar coincidencias. */
interface Contexto {
  audios: AudioSysadminDTO[];
  porId: Map<number, AudioSysadminDTO>;
  subidoPor: (a: AudioSysadminDTO) => string;
}

/** Audios de otras asesorías (o duplicados en la misma) con la misma duración, ± TOLERANCIA_SEG. */
function coincidencias(ctx: Contexto, audioId: number): AudioSysadminDTO[] {
  const base = ctx.porId.get(audioId);
  if (!base?.duracionSeg) return [];
  return ctx.audios
    .filter((a) => a.audioId !== audioId && Math.abs((a.duracionSeg ?? 0) - base.duracionSeg) <= TOLERANCIA_SEG)
    .sort((x, y) => Math.abs(x.duracionSeg - base.duracionSeg) - Math.abs(y.duracionSeg - base.duracionSeg));
}

function leerGuardado(): CasoAudios[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CasoAudios[]) : null;
  } catch {
    return null;
  }
}

function guardar(casos: CasoAudios[] | null) {
  try {
    if (casos) localStorage.setItem(STORAGE_KEY, JSON.stringify(casos));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sin almacenamiento (modo privado, cuota): la vista sigue funcionando en memoria.
  }
}

/** Acepta JSONL (una asesoría por línea) o un JSON con un arreglo. */
function parsearArchivo(texto: string): CasoAudios[] {
  const t = texto.trim();
  const crudos: unknown[] = t.startsWith('[')
    ? JSON.parse(t)
    : t
        .split('\n')
        .filter((l) => l.trim())
        .map((l) => JSON.parse(l));
  const validos = crudos.filter(
    (c): c is CasoAudios => !!c && Number.isInteger((c as CasoAudios).asesoriaId) && Array.isArray((c as CasoAudios).audios)
  );
  if (validos.length === 0) throw new Error('Ninguna línea tiene asesoriaId y audios');
  return validos.map((c) => ({ ...c, decision: c.decision ?? 'PENDIENTE' }));
}

export function AudiosAjenosPage() {
  const [casos, setCasos] = useState<CasoAudios[] | null>(leerGuardado);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Decision | 'TODOS'>('TODOS');
  const [busqueda, setBusqueda] = useState('');
  const [abiertos, setAbiertos] = useState<Set<number>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  // Corregir es solo de sysadmin; la revisión de hallazgos (correo con "+") revisa y decide.
  const esSysadmin = useAuth().user?.asesor?.rol === 'SYSADMIN';
  const [ctx, setCtx] = useState<Contexto | null>(null);
  const [errorCtx, setErrorCtx] = useState<string | null>(null);
  const hayCasos = !!casos;

  // Los audios se consultan en vivo (el JSONL no trae el archivo de Drive) para buscar coincidencias y reproducirlos.
  useEffect(() => {
    if (!hayCasos || ctx) return;
    let vivo = true;
    Promise.all([sysadminService.listarAudios(), sysadminService.listarAsesores()])
      .then(([{ audios }, { asesores }]) => {
        if (!vivo) return;
        const porCorreo = new Map(
          asesores.filter((x) => x.correo).map((x) => [x.correo.trim().replace(/^\+/, '').toLowerCase(), x.nombre])
        );
        const porAsesor = new Map(asesores.map((x) => [x.asesorId, x.nombre]));
        setCtx({
          audios,
          porId: new Map(audios.map((a) => [a.audioId, a])),
          subidoPor: (a) =>
            porCorreo.get(a.email?.trim().toLowerCase() ?? '') ?? porAsesor.get(a.asesorId) ?? a.email ?? `Asesor #${a.asesorId}`,
        });
      })
      .catch((e: any) => vivo && setErrorCtx(`No se pudieron cargar los audios para buscar coincidencias: ${e.message}`));
    return () => {
      vivo = false;
    };
  }, [hayCasos, ctx]);

  const actualizar = (nuevos: CasoAudios[] | null) => {
    setCasos(nuevos);
    guardar(nuevos);
  };

  const importar = async (file: File) => {
    setError(null);
    try {
      const nuevos = parsearArchivo(await file.text());
      // Si ya había seguimiento, se conservan decisión y notas de las asesorías que coinciden.
      const previos = new Map((casos ?? []).map((c) => [c.asesoriaId, c]));
      actualizar(
        nuevos.map((c) => {
          const p = previos.get(c.asesoriaId);
          return p?.actualizadoEn ? { ...c, decision: p.decision, nota: p.nota, actualizadoEn: p.actualizadoEn } : c;
        })
      );
    } catch (e: any) {
      setError(`No se pudo importar: ${e.message}`);
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const cambiar = (asesoriaId: number, cambios: Partial<CasoAudios>) =>
    casos &&
    actualizar(casos.map((c) => (c.asesoriaId === asesoriaId ? { ...c, ...cambios, actualizadoEn: new Date().toISOString() } : c)));

  const exportar = () => {
    if (!casos) return;
    const url = URL.createObjectURL(new Blob([casos.map((c) => JSON.stringify(c)).join('\n') + '\n'], { type: 'application/x-ndjson' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `audios_ajenos_${new Date().toISOString().slice(0, 10)}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const alternar = (id: number) =>
    setAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const lista = casos ?? [];
  const conteo = useMemo(() => {
    const c: Record<string, number> = {};
    for (const x of lista) c[x.decision ?? 'PENDIENTE'] = (c[x.decision ?? 'PENDIENTE'] ?? 0) + 1;
    return c;
  }, [lista]);
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return lista.filter((c) => {
      if (filtro !== 'TODOS' && (c.decision ?? 'PENDIENTE') !== filtro) return false;
      if (!q) return true;
      return [c.asesoriaId, c.empresaNombre, c.empresaNit, c.dueno.nombre, ...c.subieron, c.nota]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [lista, filtro, busqueda]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.03em] text-(--admin-text)">Audios de otro asesor</h1>
          <p className="mt-1 max-w-2xl text-[13px] text-(--admin-text2)">
            Revisa quién subió cada audio y cuándo se guardaron las ternas, y decide cómo corregir cada asesoría
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".jsonl,.json,application/json"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && void importar(e.target.files[0])}
          />
          <Button variant="secondary" onClick={() => inputRef.current?.click()}>
            <FileUp className="w-4 h-4 mr-2" />
            Cargar análisis
          </Button>
          <Button variant="secondary" onClick={exportar} disabled={!casos}>
            <Download className="w-4 h-4 mr-2" />
            Descargar seguimiento
          </Button>
          {casos && (
            <Button
              variant="ghost"
              onClick={() => window.confirm('¿Quitar el análisis cargado y su seguimiento de este navegador?') && actualizar(null)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Quitar
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-3 py-2 text-sm text-(--admin-red)">{error}</div>
      )}
      {errorCtx && (
        <div className="rounded-lg border border-(--admin-amber) bg-(--admin-amber-bg) px-3 py-2 text-sm text-(--admin-amber)">
          {errorCtx}
        </div>
      )}

      {!casos ? (
        <div className="rounded-(--admin-r2) border border-dashed border-(--admin-border) bg-(--admin-surface) p-10 text-center text-sm text-(--admin-text2)">
          <Users className="w-8 h-8 mx-auto mb-2 text-(--admin-text3)" />
          Carga el archivo del análisis (<span className="font-mono">audios_ajenos_AAAA-MM-DD.jsonl</span>) para empezar
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {[{ valor: 'TODOS' as const, label: 'Todas' }, ...DECISIONES].map((d) => (
                <button
                  key={d.valor}
                  onClick={() => setFiltro(d.valor)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    filtro === d.valor
                      ? 'bg-(--admin-text) text-white'
                      : 'bg-(--admin-surface2) text-(--admin-text2) hover:bg-(--admin-border)'
                  }`}
                >
                  {d.label} ({d.valor === 'TODOS' ? lista.length : (conteo[d.valor] ?? 0)})
                </button>
              ))}
            </div>
            <div className="relative md:ml-auto md:w-72">
              <Search className="w-4 h-4 text-(--admin-text3) absolute left-3 top-2.5" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Empresa, NIT, asesor, ID…"
                className="w-full bg-(--admin-surface) border border-(--admin-border) rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-(--admin-border2)"
              />
            </div>
          </div>

          <div className="bg-(--admin-surface) rounded-(--admin-r2) border border-(--admin-border) overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-(--admin-text2)">
                <thead className="bg-(--admin-surface2) text-(--admin-text3) uppercase tracking-wider">
                  <tr>
                    <th className="py-2.5 px-3">Asesoría</th>
                    <th className="py-2.5 px-3">Empresa / Dueño</th>
                    <th className="py-2.5 px-3">Audios</th>
                    <th className="py-2.5 px-3">Ternas</th>
                    <th className="py-2.5 px-3">Sugerencia</th>
                    <th className="py-2.5 px-3">Decisión</th>
                    <th className="py-2.5 px-3 text-right">Ir a</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--admin-border)">
                  {filtrados.map((c) => {
                    const abierto = abiertos.has(c.asesoriaId);
                    const tipoSug = c.sugerencia.split(':')[0];
                    const ev = c.evaluacionesInternas;
                    return (
                      <Fragment key={c.asesoriaId}>
                        <tr className="align-top">
                          <td className="py-2.5 px-3 font-mono font-semibold text-(--admin-text) whitespace-nowrap">
                            <button onClick={() => alternar(c.asesoriaId)} className="flex items-center gap-1 hover:text-(--admin-blue)">
                              {abierto ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}#{c.asesoriaId}
                            </button>
                            {c.orden != null && <span className="text-(--admin-text3) font-normal ml-4">orden {c.orden}</span>}
                          </td>
                          <td className="py-2.5 px-3 min-w-48">
                            <div className="font-semibold text-(--admin-text)">{c.empresaNombre}</div>
                            <div className="text-[11px] text-(--admin-text2)">NIT {c.empresaNit}</div>
                            <div className="text-[11px] text-(--admin-text2)">
                              Dueño: <b>{c.dueno.nombre ?? '—'}</b>
                            </div>
                            <div className="text-[11px] text-(--admin-text3)">
                              {fecha(c.fechaInicio)} → {fecha(c.fechaFin)}
                            </div>
                          </td>
                          <td className="py-2.5 px-3 min-w-44">
                            <div>
                              <b className="text-(--admin-red)">{c.resumenAudios.ajenosVigentes}</b> de {c.resumenAudios.vigentes} vigentes
                              de otra persona
                            </div>
                            <div className="text-[11px] text-(--admin-text2)">Subieron: {c.subieron.join(', ') || '—'}</div>
                            {ctx &&
                              (() => {
                                const n = c.audios.filter((a) => coincidencias(ctx, a.audioId).length > 0).length;
                                return n > 0 ? (
                                  <div className="mt-1 flex items-center gap-1 text-[11px] text-(--admin-text3)">
                                    <Copy className="w-3 h-3" /> {n} audio(s) con duración igual en otra parte
                                  </div>
                                ) : null;
                              })()}
                            {c.resumenAudios.primerAudioAjeno && (
                              <div className="text-[11px] text-(--admin-text3)">
                                1.er audio ajeno: {fecha(c.resumenAudios.primerAudioAjeno)}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <div
                              className="flex h-2 w-28 rounded-full overflow-hidden bg-(--admin-surface2)"
                              title="Antes / después del 1.er audio ajeno"
                            >
                              <div
                                className="bg-(--admin-text2)"
                                style={{ width: `${(ev.antesDelPrimerAudioAjeno / Math.max(1, ev.ternasEvaluadas)) * 100}%` }}
                              />
                              <div className="bg-(--admin-red) flex-1" />
                            </div>
                            <div className="text-[11px] mt-1">
                              <span className="text-(--admin-blue)">{ev.antesDelPrimerAudioAjeno} antes</span> ·{' '}
                              <span className="text-(--admin-red)">{ev.despues} después</span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 max-w-xs">
                            <Sello>{SUGERENCIAS[tipoSug]?.label ?? tipoSug}</Sello>
                            <div className="mt-1 text-[11px] text-(--admin-text2)">{c.sugerencia.split(':').slice(1).join(':').trim()}</div>
                          </td>
                          <td className="py-2.5 px-3 min-w-52 space-y-1.5">
                            <select
                              value={c.decision ?? 'PENDIENTE'}
                              onChange={(e) => cambiar(c.asesoriaId, { decision: e.target.value as Decision })}
                              className="w-full border border-(--admin-border) rounded-md px-2 py-1 text-xs"
                              aria-label="Decisión"
                            >
                              {DECISIONES.map((d) => (
                                <option key={d.valor} value={d.valor}>
                                  {d.label}
                                </option>
                              ))}
                            </select>
                            <textarea
                              defaultValue={c.nota ?? ''}
                              onBlur={(e) => e.target.value !== (c.nota ?? '') && cambiar(c.asesoriaId, { nota: e.target.value })}
                              placeholder="Nota…"
                              rows={2}
                              className="w-full border border-(--admin-border) rounded-md px-2 py-1 text-xs resize-y"
                            />
                            {c.actualizadoEn && (
                              <div className="text-[11px] text-(--admin-text3)">Actualizado {fechaHora(c.actualizadoEn)}</div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right space-y-1 whitespace-nowrap">
                            {c.actaUrl && (
                              <a
                                href={c.actaUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-end gap-1 text-(--admin-blue) hover:underline"
                              >
                                Acta <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            {esSysadmin && (
                              <Link to="/admin/correcciones-asesorias" className="block text-(--admin-blue) hover:underline">
                                Corregir →
                              </Link>
                            )}
                          </td>
                        </tr>
                        {abierto && (
                          <tr className="bg-(--admin-surface2)">
                            <td colSpan={7} className="px-3 py-3">
                              <LineaDeTiempo caso={c} ctx={ctx} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {filtrados.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-(--admin-text3)">
                        No hay asesorías con estos filtros
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-[11px] text-(--admin-text3)">
            La fecha de cada terna es la última vez que se guardó, así que el corte antes y después es orientativo
          </p>
        </>
      )}
    </div>
  );
}

/** Audios y ternas en orden cronológico, para ver dónde entra el otro asesor. */
function LineaDeTiempo({ caso, ctx }: { caso: CasoAudios; ctx: Contexto | null }) {
  // Las ternas se agrupan por día para no listar decenas de filas.
  const ternasPorDia = new Map<string, number>();
  for (const t of caso.evaluacionesInternas.ternas) {
    const d = t.fechaEvaluacion.slice(0, 10);
    ternasPorDia.set(d, (ternasPorDia.get(d) ?? 0) + 1);
  }
  const eventos = [
    ...caso.audios.map((a) => ({ fecha: a.fechaGrabacion ?? '', tipo: 'audio' as const, a })),
    ...[...ternasPorDia].map(([d, n]) => ({ fecha: `${d}T23:59:59`, tipo: 'ternas' as const, n })),
  ].sort((x, y) => x.fecha.localeCompare(y.fecha));

  return (
    <div className="grid md:grid-cols-2 gap-4 text-xs">
      <section>
        <h4 className="font-semibold text-(--admin-text) mb-2">Línea de tiempo</h4>
        <ol className="space-y-1 border-l border-(--admin-border) pl-3">
          {eventos.map((e, idx) =>
            e.tipo === 'audio' ? (
              <li key={`a${e.a.audioId}`} className={e.a.vigente ? '' : 'opacity-50'}>
                <span className="font-mono text-(--admin-text3)">{fechaHora(e.a.fechaGrabacion)}</span>{' '}
                <Sello tono={e.a.esDelDueno ? 'neutro' : 'alerta'}>{e.a.esDelDueno ? 'Dueño' : 'Otra persona'}</Sello> Audio #{e.a.audioId}{' '}
                de <b>{e.a.subidoPor ?? '—'}</b> · {duracion(e.a.duracionSeg)}
                {!e.a.vigente && <span className="text-(--admin-text2)"> · {estadoAudio(e.a.estado)}</span>}
              </li>
            ) : (
              <li key={`t${idx}`} className="text-(--admin-blue)">
                <span className="font-mono text-(--admin-text3)">{fecha(e.fecha)}</span> {e.n} terna(s) guardadas
              </li>
            )
          )}
        </ol>
      </section>
      <section className="space-y-1">
        <h4 className="font-semibold text-(--admin-text) mb-2">Evaluaciones internas</h4>
        <div>
          Ternas guardadas: <b>{caso.evaluacionesInternas.ternasEvaluadas}</b>
        </div>
        <div>
          Primera: {fechaHora(caso.evaluacionesInternas.primeraFecha)} · Última: {fechaHora(caso.evaluacionesInternas.ultimaFecha)}
        </div>
        <div>
          Antes del primer audio ajeno: <b className="text-(--admin-blue)">{caso.evaluacionesInternas.antesDelPrimerAudioAjeno}</b> ·
          Después: <b className="text-(--admin-red)">{caso.evaluacionesInternas.despues}</b>
        </div>
        <div className="text-(--admin-text2) pt-2">
          Total de audios: {caso.resumenAudios.total} ({caso.resumenAudios.vigentes} vigentes)
        </div>
      </section>
      <section className="md:col-span-2 space-y-2">
        <h4 className="font-semibold text-(--admin-text)">Coincidencias por duración (± {TOLERANCIA_SEG} s)</h4>
        {!ctx ? (
          <div className="text-(--admin-text3)">Cargando audios…</div>
        ) : (
          caso.audios.map((a) => <Coincidencias key={a.audioId} audio={a} caso={caso} ctx={ctx} />)
        )}
      </section>
    </div>
  );
}

/** Un audio de la asesoría, los que tienen su misma duración en otra parte y el reproductor de cada uno. */
function Coincidencias({ audio, caso, ctx }: { audio: AudioCaso; caso: CasoAudios; ctx: Contexto }) {
  const [abierto, setAbierto] = useState(false);
  const iguales = coincidencias(ctx, audio.audioId);
  const propio = ctx.porId.get(audio.audioId);
  // Lo más probable: que quien subió el audio ajeno lo haya subido también en una asesoría propia.
  const delMismo = (o: AudioSysadminDTO) => ctx.subidoPor(o) === audio.subidoPor;
  return (
    <div
      className={`rounded-lg border bg-(--admin-surface) p-2 ${iguales.length ? 'border-(--admin-border2)' : 'border-(--admin-border)'}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Sello tono={audio.esDelDueno ? 'neutro' : 'alerta'}>{audio.esDelDueno ? 'Dueño' : 'Otra persona'}</Sello>
        <span>
          Audio #{audio.audioId} de <b>{audio.subidoPor ?? '—'}</b> · {duracion(audio.duracionSeg)} · {fecha(audio.fechaGrabacion)}
          {!audio.vigente && <span className="text-(--admin-text2)"> · {estadoAudio(audio.estado)}</span>}
        </span>
        <span className={iguales.length ? 'text-(--admin-text3) font-semibold' : 'text-(--admin-text3)'}>
          {iguales.length ? `${iguales.length} coincidencia(s)` : 'Sin coincidencias'}
        </span>
        <button onClick={() => setAbierto(!abierto)} className="ml-auto flex items-center gap-1 text-(--admin-blue) hover:underline">
          <Headphones className="w-3 h-3" /> {abierto ? 'Ocultar' : 'Escuchar y comparar'}
        </button>
      </div>
      {abierto && (
        <div className="mt-2 space-y-2">
          {propio?.adjunteDelAudio && (
            <AudioPlayer
              fileIdOrUrl={propio.adjunteDelAudio}
              initialDuration={propio.duracionSeg}
              compact
              title={`#${audio.audioId} · asesoría #${caso.asesoriaId}`}
            />
          )}
          {iguales.map((o) => (
            <div
              key={o.audioId}
              className={`rounded-md border p-2 ${delMismo(o) ? 'border-(--admin-red) bg-(--admin-red-bg)' : 'border-(--admin-border)'}`}
            >
              <div className="mb-1 flex flex-wrap gap-x-2 text-[11px] text-(--admin-text2)">
                <b className="text-(--admin-text)">Audio #{o.audioId}</b>
                <span>
                  asesoría #{o.asesoriaId}
                  {o.asesoriaId === caso.asesoriaId && ' (misma)'}
                </span>
                <span>NIT {o.empresaId}</span>
                <span>
                  subió <b>{ctx.subidoPor(o)}</b>
                </span>
                <span>{duracion(o.duracionSeg)}</span>
                <span>{fecha(o.fechaGrabacion)}</span>
                {o.estado && <span>{estadoAudio(o.estado)}</span>}
                {delMismo(o) && <Sello tono="alerta">Misma persona</Sello>}
              </div>
              <AudioPlayer fileIdOrUrl={o.adjunteDelAudio} initialDuration={o.duracionSeg} compact />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
